import { laufzeitApi } from '@/api/services/laufzeitApi';
import { NumericTextBox, Spinner } from '@/components/ui';
import { createChinaAppSysLaufzeit, type ChinaAppSysLaufzeit } from '@/types/entities';
import { LaufzeitSchritt } from '@/types/enums';

import { AdminNotice } from '../AdminNotice';
import { useAdminList } from './adminList';

/** Laufzeiten — Port von `IwmAdminListLaufzeiten.razor` (REQ-29 §4). */
const load = () => laufzeitApi.get();

/**
 * Die vier Schritte des Enums in der Reihenfolge des Transportwegs. Ohne einen von ihnen
 * weist der Server das Anlegen und Ändern einer Bestellposition zurück
 * (`OrderPosition.RecalcLiefertermin` wirft `ChinaAppUserFriendlyException`).
 */
const STEPS: readonly LaufzeitSchritt[] = [
  LaufzeitSchritt.Vorlauf,
  LaufzeitSchritt.Transport,
  LaufzeitSchritt.Nachlauf,
  LaufzeitSchritt.Anlieferung,
];

/** Der Wortlaut steht so in REQ-29 §4. */
const CHANGE_WARNING = 'Eine Änderung wirkt sich auf alle künftigen Liefertermin-Berechnungen aus.';

export function AdminLaufzeitList() {
  const { rows, setRows, loading, error, reload } = useAdminList(load);

  const entryOf = (step: LaufzeitSchritt): ChinaAppSysLaufzeit | undefined =>
    rows.find((row) => row.SchrittName === step);

  const missing = STEPS.filter((step) => entryOf(step) === undefined);

  const replace = (row: ChinaAppSysLaufzeit) => {
    setRows((current) => current.map((entry) => (entry.Id === row.Id ? row : entry)));
  };

  /**
   * Speichern beim Verlassen des Feldes. Ein fehlender Schritt wird angelegt — das ist das
   * heutige Verhalten für `Id 0`; `POST laufzeiten` antwortet nur mit der Id, deshalb wird
   * danach neu geladen.
   */
  const save = async (step: LaufzeitSchritt, dauer: number) => {
    const entry = entryOf(step);
    if (entry === undefined) {
      await laufzeitApi.create({
        ...createChinaAppSysLaufzeit(),
        SchrittName: step,
        DauerTage: dauer,
      });
      await reload();
      return;
    }
    const next = { ...entry, DauerTage: dauer };
    replace(next);
    const result = await laufzeitApi.put(entry.Id, next);
    replace(result ?? entry);
  };

  if (loading) {
    return (
      <div className="iwm-state-loading">
        <span className="iwm-state-spinner">
          <Spinner size={20} />
        </span>
        Wird geladen …
      </div>
    );
  }

  return (
    <section className="iwm-admin-sublist">
      {error === null ? null : (
        <AdminNotice tone="danger">Die Laufzeiten konnten nicht geladen werden.</AdminNotice>
      )}

      <div className="iwm-admin-sublist-wrap">
        <table className="iwm-admin-sublist-table">
          <thead>
            <tr>
              <th scope="col">Step</th>
              <th scope="col">Duration in Days</th>
            </tr>
          </thead>
          <tbody>
            {STEPS.map((step) => {
              const entry = entryOf(step);
              return (
                <tr key={step}>
                  {/* Die SfGrid zeigt den Enum-Namen (`ToString()`), nicht den Zahlenwert. */}
                  <td>{LaufzeitSchritt[step]}</td>
                  <td className="iwm-admin-sublist-number">
                    <NumericTextBox
                      className="iwm-admin-cell-field"
                      label={`Duration in Days: ${LaufzeitSchritt[step]}`}
                      format="d"
                      min={0}
                      value={entry?.DauerTage ?? 0}
                      onChange={(value) => {
                        void save(step, value ?? 0);
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {missing.map((step) => (
        <AdminNotice key={step} tone="danger">
          {`Für ${LaufzeitSchritt[step]} ist keine Laufzeit hinterlegt. Ohne sie lässt sich keine Bestellposition anlegen oder ändern.`}
        </AdminNotice>
      ))}

      <AdminNotice tone="warning">{CHANGE_WARNING}</AdminNotice>

      <AdminNotice>
        <p className="iwm-admin-notice-text">
          Der Server leitet den Liefertermin einer Bestellposition ab; die erste zutreffende Regel
          gewinnt:
        </p>
        <ol className="iwm-admin-notice-steps">
          <li>Das Sage-Buchungsdatum, wenn es gesetzt ist.</li>
          <li>Der Liefertermin des zugewiesenen Containers.</li>
          <li>Ankunftsdatum Buchung plus Nachlauf plus Anlieferung.</li>
          <li>PSI plus Vorlauf plus Transport plus Nachlauf plus Anlieferung.</li>
          <li>Sonst der 31.12. des Folgejahres.</li>
        </ol>
        <p className="iwm-admin-notice-text">Den Liefertermin eines Containers rechnet er so:</p>
        <ol className="iwm-admin-notice-steps">
          <li>Anlieferdatum Empfänger plus Anlieferung.</li>
          <li>Frühestes Anlieferdatum plus Anlieferung.</li>
          <li>ETA plus Anlieferung plus Nachlauf.</li>
          <li>ETD plus Anlieferung plus Nachlauf plus Transport.</li>
          <li>
            Spätestes PSI seiner Positionen plus Vorlauf plus Transport plus Nachlauf plus
            Anlieferung.
          </li>
          <li>Sonst der 31.12. des Folgejahres.</li>
        </ol>
        <p className="iwm-admin-notice-text">
          Ändert sich dabei der Liefertermin einer Bestellposition, merkt der Server sie für den
          Export nach Sage vor.
        </p>
      </AdminNotice>
    </section>
  );
}

export default AdminLaufzeitList;
